/**
 * Format helpers for the Agency voice (DESIGN_SYSTEM section 14, BUILD_PLAN section 7).
 * Timestamps `YYYY-MM-DD HH:MMZ`, clock `HH:MM:SS ET`, relative `4d` / `3d ago`, ratio `47/48`,
 * delta `+12` / `-2 pts (24h)`, counts comma-grouped, empty `--`, money from integer micro-USD.
 * All functions are pure and never throw on null input: null / undefined render as `--`.
 */

export const EMPTY = '--';

export type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** `--` for null, undefined and empty strings; otherwise the value as a string. */
export function fmtEmpty(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'string' && value.trim() === '') return EMPTY;
  return String(value);
}

/** `YYYY-MM-DD` in UTC. */
export function fmtDate(value: DateInput): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** `YYYY-MM-DD HH:MMZ` in UTC (section 14). Pass `seconds: true` for `HH:MM:SSZ`. */
export function fmtTimestamp(value: DateInput, options?: { seconds?: boolean }): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  const hm = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  const time = options?.seconds ? `${hm}:${pad2(d.getUTCSeconds())}` : hm;
  return `${fmtDate(d)} ${time}Z`;
}

const TZ_LABEL_COLLAPSE: Record<string, string> = {
  EST: 'ET',
  EDT: 'ET',
  CST: 'CT',
  CDT: 'CT',
  MST: 'MT',
  MDT: 'MT',
  PST: 'PT',
  PDT: 'PT',
  GMT: 'UTC',
};

export interface ClockOptions {
  /** IANA time zone, e.g. `America/New_York`. Default: UTC. */
  timeZone?: string;
  /** Explicit label appended after the time. Default: derived from the zone (`ET`, `PT`, `UTC`). */
  label?: string;
  /** Omit seconds (`HH:MM ET`). */
  seconds?: boolean;
}

/** `HH:MM:SS ET` (section 14). 24-hour clock, time zone label collapsed to the region form. */
export function fmtClock(value: DateInput, options: ClockOptions = {}): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  const timeZone = options.timeZone ?? 'UTC';
  const showSeconds = options.seconds ?? true;
  let time: string;
  let label = options.label;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).formatToParts(d);
    const get = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((p) => p.type === type)?.value ?? '00';
    const hour = get('hour') === '24' ? '00' : get('hour');
    time = showSeconds ? `${hour}:${get('minute')}:${get('second')}` : `${hour}:${get('minute')}`;
    if (!label) {
      const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'UTC';
      label = TZ_LABEL_COLLAPSE[raw] ?? raw;
    }
  } catch {
    time = showSeconds
      ? `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`
      : `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
    label = label ?? 'UTC';
  }
  return `${time} ${label}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/** Compact age or lead time: `14m`, `4d`, `3mo`, `2y`; under a minute renders `now`. */
export function fmtAge(value: DateInput, now: DateInput = Date.now()): string {
  const d = toDate(value);
  const n = toDate(now);
  if (!d || !n) return EMPTY;
  const diff = Math.abs(n.getTime() - d.getTime());
  if (diff < MINUTE) return 'now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;
  if (diff < MONTH) return `${Math.floor(diff / DAY)}d`;
  if (diff < YEAR) return `${Math.floor(diff / MONTH)}mo`;
  return `${Math.floor(diff / YEAR)}y`;
}

/**
 * Relative time in the Agency voice (section 14): past values read `3d ago`, future values read
 * `4d` (as in "due 4d"), values under a minute read `now`.
 */
export function fmtRelative(value: DateInput, now: DateInput = Date.now()): string {
  const d = toDate(value);
  const n = toDate(now);
  if (!d || !n) return EMPTY;
  const age = fmtAge(d, n);
  if (age === 'now' || age === EMPTY) return age;
  return d.getTime() < n.getTime() ? `${age} ago` : age;
}

/** Comma-grouped integer count (section 4.4): `4,212,882`. Non-finite values render `--`. */
export function fmtCount(value: number | bigint | null | undefined): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'number' && !Number.isFinite(value)) return EMPTY;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

/** `47/48` or `11/13 controls`. A missing denominator renders `--` (frameworks rule: Unknown). */
export function fmtRatio(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  unit?: string,
): string {
  if (numerator === null || numerator === undefined) return EMPTY;
  if (denominator === null || denominator === undefined || !Number.isFinite(denominator)) return EMPTY;
  const core = `${fmtCount(numerator)}/${fmtCount(denominator)}`;
  return unit ? `${core} ${unit}` : core;
}

/** Integer percent with the sign: `72%`. Input is a ratio 0..1. */
export function fmtPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return EMPTY;
  return `${(ratio * 100).toFixed(digits)}%`;
}

export interface DeltaOptions {
  /** Unit appended after the number: `pts`, `%`. */
  unit?: string;
  /** Window appended in parentheses: `24h`, `7d`. */
  window?: string;
  /** Decimal places. Default 0. */
  digits?: number;
}

/** Signed delta: `+12`, `-4`, `-2 pts (24h)`, `0`. */
export function fmtDelta(value: number | null | undefined, options: DeltaOptions = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  const digits = options.digits ?? 0;
  const abs = Math.abs(value);
  const body = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(abs);
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  let out = `${sign}${body}`;
  if (options.unit) out += options.unit === '%' ? '%' : ` ${options.unit}`;
  if (options.window) out += ` (${options.window})`;
  return out;
}

export interface MoneyOptions {
  /** `$4.2M` / `$12.4K` for large values. Default false: `$217,400`. */
  compact?: boolean;
}

const MICRO = 1_000_000;

/**
 * USD from an integer micro-USD amount (spec section 17: money is integer micro-currency).
 * `$12,400.00` for amounts of a dollar or more, `$0.0042` below a dollar (4 significant decimals),
 * `$0` for zero, `$4.2M` with `compact`.
 */
export function fmtMicroUsd(micro: number | bigint | null | undefined, options: MoneyOptions = {}): string {
  if (micro === null || micro === undefined) return EMPTY;
  const n = typeof micro === 'bigint' ? Number(micro) : micro;
  if (!Number.isFinite(n)) return EMPTY;
  const negative = n < 0;
  const dollars = Math.abs(n) / MICRO;
  const sign = negative ? '-' : '';
  if (dollars === 0) return '$0';
  if (options.compact) {
    if (dollars >= 1_000_000) return `${sign}$${trimZeros((dollars / 1_000_000).toFixed(1))}M`;
    if (dollars >= 10_000) return `${sign}$${trimZeros((dollars / 1_000).toFixed(1))}K`;
    return `${sign}$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(dollars))}`;
  }
  if (dollars >= 1) {
    return `${sign}$${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars)}`;
  }
  // sub-dollar: show up to 4 significant decimals without exponent notation
  const fixed = dollars.toFixed(6);
  const trimmed = trimZeros(fixed);
  const [whole, frac = ''] = trimmed.split('.');
  const shown = frac.length > 4 ? frac.slice(0, 4) : frac;
  return `${sign}$${whole}${shown ? `.${shown}` : ''}`;
}

function trimZeros(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** `1.2 MB` (1024-based). Whole bytes below 1 KB: `812 B`. */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return EMPTY;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = value >= 100 ? 0 : 1;
  return `${trimZeros(value.toFixed(digits))} ${BYTE_UNITS[unit] ?? 'B'}`;
}

/** Duration from milliseconds: `18s`, `2m 05s`, `1h 12m`, `3d 4h`, `420ms`. */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return EMPTY;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${pad2(seconds)}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${pad2(remMinutes)}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h`;
}

/** Two-letter uppercase initials from a display name: `Amir Tarighat` -> `AT`. */
export function initials(name: string | null | undefined, fallback = '--'): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : parts[0]?.[1] ?? '';
  return `${first}${last}`.toUpperCase();
}
